import type { InstantSearchProps } from "react-instantsearch";
import { MARKETPLACE_QUERY_PARAM, splitUrl } from "@/lib/search-params";

type UiState = NonNullable<InstantSearchProps["initialUiState"]>;
type IndexUiState = UiState[string];

/**
 * /marketplace URL grammar. All marketplace search state lives in InstantSearch
 * uiState, so the codec maps straight between uiState and params:
 *
 *   ?q=surf&type=services&price=10-50&sort=price_asc
 *
 * `price` carries canonical USD decimals, not the viewer's display currency —
 * display conversion is per-viewer and drifts with exchange rates, so a
 * display-unit URL would mean different things to different recipients. Going
 * through `range.price_cents` (integer cents, `min:max`, either side blank)
 * also bypasses the per-bound display rounding entirely.
 */

const TYPE_PARAM = "type";
const PRICE_PARAM = "price";
const SORT_PARAM = "sort";

export const MARKETPLACE_SEARCH_PARAM_KEYS = [MARKETPLACE_QUERY_PARAM, TYPE_PARAM, PRICE_PARAM, SORT_PARAM] as const;
export const MARKETPLACE_CONTINUOUS_KEYS = [MARKETPLACE_QUERY_PARAM, PRICE_PARAM] as const;

// Facet value ↔ URL token, so the URL never leaks model class names.
const TYPE_TO_PARAM: Record<string, string> = { Product: "products", Service: "services" };
const PARAM_TO_TYPE: Record<string, string> = { products: "Product", services: "Service" };

// Sort index name ↔ URL token, so the URL never leaks index names.
const SORT_TO_PARAM: Record<string, string> = {
  listings_price_asc: "price_asc",
  listings_price_desc: "price_desc",
  listings_top_reviewed: "top_reviewed",
  listings_newest: "newest",
};
const PARAM_TO_SORT: Record<string, string> = Object.fromEntries(
  Object.entries(SORT_TO_PARAM).map(([sort, param]) => [param, sort]),
);

const PRICE_SHAPE = /^(\d+(?:\.\d{1,2})?)?-(\d+(?:\.\d{1,2})?)?$/;

function centsToDecimal(cents: string): string {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "";
  return n % 100 === 0 ? String(n / 100) : (n / 100).toFixed(2);
}

/** `"1000:5000"` (cents) → `"10-50"` (USD); either side may be blank. */
function rangeToPriceParam(range: string | undefined): string | undefined {
  if (!range) return undefined;
  const [min = "", max = ""] = range.split(":");
  if (!min && !max) return undefined;
  return `${min && centsToDecimal(min)}-${max && centsToDecimal(max)}`;
}

/** `"10-50"` (USD) → `"1000:5000"` (cents); `undefined` when malformed or empty. */
function priceParamToRange(raw: string | null): string | undefined {
  if (!raw) return undefined;
  const match = PRICE_SHAPE.exec(raw);
  if (!match || (!match[1] && !match[2])) return undefined;
  const min = match[1] ? String(Math.round(Number(match[1]) * 100)) : "";
  const max = match[2] ? String(Math.round(Number(match[2]) * 100)) : "";
  return `${min}:${max}`;
}

export interface MarketplaceSearchParams {
  query?: string;
  /** Facet value (`Product` / `Service`). */
  type?: string;
  /** `range.price_cents` shape: `"min:max"` in integer cents. */
  priceRange?: string;
  /** Sort index name (`listings_price_asc` …). */
  sort?: string;
}

/** Whether the URL carries any owned search param — such a mount must not
 *  render the default-state seed (its own results replace it). */
export function hasMarketplaceSearchParams(url: string): boolean {
  const { params } = splitUrl(url);
  return MARKETPLACE_SEARCH_PARAM_KEYS.some((key) => params.has(key));
}

export function parseMarketplaceSearchParams(url: string): MarketplaceSearchParams {
  const { params } = splitUrl(url);
  const query = params.get(MARKETPLACE_QUERY_PARAM);
  const type = PARAM_TO_TYPE[params.get(TYPE_PARAM) ?? ""];
  const priceRange = priceParamToRange(params.get(PRICE_PARAM));
  const sort = PARAM_TO_SORT[params.get(SORT_PARAM) ?? ""];
  return {
    ...(query ? { query } : {}),
    ...(type ? { type } : {}),
    ...(priceRange ? { priceRange } : {}),
    ...(sort ? { sort } : {}),
  };
}

/** The parsed state as `initialUiState`; `undefined` when the URL carries nothing. */
export function toMarketplaceUiState(state: MarketplaceSearchParams, indexName: string): UiState | undefined {
  const indexUiState: IndexUiState = {};
  if (state.query) indexUiState.query = state.query;
  if (state.type) indexUiState.menu = { type: state.type };
  if (state.priceRange) indexUiState.range = { price_cents: state.priceRange };
  if (state.sort) indexUiState.sortBy = state.sort;
  if (Object.keys(indexUiState).length === 0) return undefined;
  return { [indexName]: indexUiState };
}

export function serializeMarketplaceSearchParams(indexUiState: IndexUiState): Record<string, string | undefined> {
  return {
    [MARKETPLACE_QUERY_PARAM]: indexUiState.query || undefined,
    [TYPE_PARAM]: TYPE_TO_PARAM[indexUiState.menu?.type ?? ""],
    [PRICE_PARAM]: rangeToPriceParam(indexUiState.range?.price_cents),
    [SORT_PARAM]: SORT_TO_PARAM[indexUiState.sortBy ?? ""],
  };
}
